import {
  CHILD_AGE_MONTH_OPTIONS,
  CHILD_AGE_YEAR_OPTIONS,
} from "helpers/dataConversion/convertClassAgeRange";
import {
  DAY_KEYS,
  DAY_LABELS,
  DEFAULT_CLASS_SCHEDULE_FILTER,
  isClassScheduleFilterActive,
  TIME_OF_DAY_OPTIONS,
} from "helpers/jackrabbit/classScheduleFilter";
import {
  trackScheduleFilterAge,
  trackScheduleFilterClear,
  trackScheduleFilterDay,
  trackScheduleFilterOpenSpots,
  trackScheduleFilterTime,
} from "helpers/analytics/trackScheduleFilter";
import useTrackWhenVisible from "hooks/useTrackWhenVisible";
import { SCHEDULE_FILTER_HASH } from "helpers/jackrabbit/scheduleFilterSearchParams";
import ProgramAgeGuidance from "./ProgramAgeGuidance";
import "./classScheduleFilter.css";

const ClassScheduleFilter = ({
  filter,
  onChange,
  matchSummary,
  hasLoadedSections,
  isSettling = false,
  programName,
  ageMismatch,
  filterUrgencySummary,
}) => {
  const isActive = isClassScheduleFilterActive(filter);
  const totalMatches = matchSummary.matchCount ?? 0;
  const viewRef = useTrackWhenVisible(
    "schedule_filter_view",
    { item_name: programName, location: window.location.pathname },
    0.35
  );

  const toggleDay = (day) => {
    const selected = !filter.days.includes(day);
    const days = selected
      ? [...filter.days, day]
      : filter.days.filter((d) => d !== day);
    const nextFilter = { ...filter, days };
    trackScheduleFilterDay(programName, day, selected, nextFilter);
    onChange(nextFilter);
  };

  const setTimeOfDay = (timeOfDay) => {
    const nextFilter = { ...filter, timeOfDay };
    trackScheduleFilterTime(programName, timeOfDay, nextFilter);
    onChange(nextFilter);
  };

  const setChildAgeYears = (event) => {
    const value = event.target.value;
    const nextFilter = {
      ...filter,
      childAgeYears: value === "" ? null : Number(value),
      childAgeMonths: value === "" ? 0 : filter.childAgeMonths,
    };
    trackScheduleFilterAge(programName, nextFilter);
    onChange(nextFilter);
  };

  const setChildAgeMonths = (event) => {
    const nextFilter = {
      ...filter,
      childAgeMonths: Number(event.target.value),
    };
    trackScheduleFilterAge(programName, nextFilter);
    onChange(nextFilter);
  };

  const clearFilters = (clearLocation) => {
    trackScheduleFilterClear(programName, clearLocation, filter);
    onChange({ ...DEFAULT_CLASS_SCHEDULE_FILTER });
  };

  const renderUrgencyNudge = () => {
    if (!isActive || !filterUrgencySummary || totalMatches === 0) return null;

    const { highCount, mediumCount } = filterUrgencySummary;

    if (highCount > 0) {
      return (
        <p className="class-schedule-filter__urgency-nudge" role="status">
          {highCount === 1
            ? "1 matching class has very limited spots — register soon to hold your spot."
            : `${highCount} matching classes have very limited spots — register soon to hold your spot.`}
        </p>
      );
    }

    if (mediumCount > 0) {
      return (
        <p className="class-schedule-filter__urgency-nudge" role="status">
          Some matching classes are almost full — register soon to hold your spot.
        </p>
      );
    }

    return null;
  };

  const renderSummary = () => {
    if (!isActive) return null;

    if (!hasLoadedSections || isSettling) {
      return (
        <p className="class-schedule-filter__summary" aria-live="polite">
          Updating schedules…
        </p>
      );
    }

    if (totalMatches === 0) {
      if (ageMismatch) {
        return (
          <p
            className="class-schedule-filter__summary class-schedule-filter__summary--empty"
            aria-live="polite"
          >
            No {programName} classes for this age.
          </p>
        );
      }

      return (
        <p className="class-schedule-filter__summary class-schedule-filter__summary--empty" aria-live="polite">
          No classes match these filters. Try a different age, fewer days, or a
          broader start time, or{" "}
          <button
            type="button"
            className="class-schedule-filter__clear-inline"
            onClick={() => clearFilters("inline_summary")}
          >
            clear filters
          </button>
          .
        </p>
      );
    }

    return (
      <p className="class-schedule-filter__summary" aria-live="polite">
        {totalMatches}{" "}
        {totalMatches === 1 ? "class matches" : "classes match"} your filters
      </p>
    );
  };

  return (
    <section
      ref={viewRef}
      id={SCHEDULE_FILTER_HASH}
      className="class-schedule-filter"
      aria-labelledby="class-schedule-filter-heading"
    >
      <div className="class-schedule-filter__inner">
        <header className="class-schedule-filter__header">
          <h2 id="class-schedule-filter-heading" className="class-schedule-filter__title">
            Find a class that fits your schedule
          </h2>
          <p className="class-schedule-filter__subtitle">
            Select your child&apos;s age, days, and start times. Schedules update
            a moment after you finish adjusting.
          </p>
        </header>

        <fieldset className="class-schedule-filter__group">
          <legend className="class-schedule-filter__legend">
            How old is your child?
          </legend>
          <p className="class-schedule-filter__hint">
            Shows classes open to your child&apos;s age.
          </p>
          <div className="class-schedule-filter__age-inputs">
            <label className="class-schedule-filter__age-field">
              <span className="class-schedule-filter__age-label">Years</span>
              <select
                className="class-schedule-filter__select"
                value={
                  filter.childAgeYears === null ? "" : String(filter.childAgeYears)
                }
                onChange={setChildAgeYears}
              >
                {CHILD_AGE_YEAR_OPTIONS.map((option) => (
                  <option key={option.label} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label
              className={`class-schedule-filter__age-field${
                filter.childAgeYears === null
                  ? " class-schedule-filter__age-field--disabled"
                  : ""
              }`}
            >
              <span className="class-schedule-filter__age-label">Months</span>
              <select
                className="class-schedule-filter__select"
                value={String(filter.childAgeMonths ?? 0)}
                onChange={setChildAgeMonths}
                disabled={filter.childAgeYears === null}
                aria-disabled={filter.childAgeYears === null}
              >
                {CHILD_AGE_MONTH_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <ProgramAgeGuidance
            programName={programName}
            ageMismatch={ageMismatch}
          />
        </fieldset>

        <fieldset className="class-schedule-filter__group">
          <legend className="class-schedule-filter__legend">Which days work for you?</legend>
          <p className="class-schedule-filter__hint">
            Select one or more days — we&apos;ll show classes that meet on any of
            them.
          </p>
          <div className="class-schedule-filter__chips">
            {DAY_KEYS.map((day) => {
              const selected = filter.days.includes(day);
              return (
                <button
                  key={day}
                  type="button"
                  className={`class-schedule-filter__chip${
                    selected ? " class-schedule-filter__chip--selected" : ""
                  }`}
                  aria-pressed={selected}
                  onClick={() => toggleDay(day)}
                >
                  {DAY_LABELS[day]}
                </button>
              );
            })}
          </div>
        </fieldset>

        <fieldset className="class-schedule-filter__group">
          <legend className="class-schedule-filter__legend">When does the class start?</legend>
          <div
            className="class-schedule-filter__time-options"
            role="group"
            aria-label="Class start time"
          >
            {TIME_OF_DAY_OPTIONS.map((option) => {
              const selected = filter.timeOfDay === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  className={`class-schedule-filter__time-option${
                    selected ? " class-schedule-filter__time-option--selected" : ""
                  }`}
                  aria-pressed={selected}
                  onClick={() => setTimeOfDay(option.id)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </fieldset>

        <label className="class-schedule-filter__checkbox">
          <input
            type="checkbox"
            checked={filter.openSpotsOnly}
            onChange={(event) => {
              const nextFilter = {
                ...filter,
                openSpotsOnly: event.target.checked,
              };
              trackScheduleFilterOpenSpots(
                programName,
                event.target.checked,
                nextFilter
              );
              onChange(nextFilter);
            }}
          />
          <span>Only show classes with open spots</span>
        </label>

        <footer className="class-schedule-filter__footer">
          {renderUrgencyNudge()}
          {renderSummary()}
          {isActive && (
            <button
              type="button"
              className="class-schedule-filter__clear"
              onClick={() => clearFilters("footer")}
            >
              Clear filters
            </button>
          )}
        </footer>
      </div>
    </section>
  );
};

export default ClassScheduleFilter;
